import {
  ComplianceProgramStep,
  TimelineEntryFragment,
  TimelineEntryKind,
  TimelineRecipientRule,
  TimelineSkipReason,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";

/**
 * The words the Timeline uses, in one place.
 *
 * Kept out of the component file so that the setup form's panels can read them
 * too: a header counting the messages and the rows badging them have to agree
 * about what counts as a send.
 */

/** One dated row of the plan, whichever query fetched it. */
export type TimelineRow = TimelineEntryFragment;

/**
 * The rows that send one message. Kickoff is deliberately not one of them: it is
 * the merge of every message that goes out that day, and those messages are listed
 * under the same date, so counting them twice on one day reads as two sends.
 */
export const sendingKinds: TimelineEntryKind[] = [
  TimelineEntryKind.Notification,
  TimelineEntryKind.Reminder,
];

/** Why one person hears nothing. */
export const skipReasonLabels: Record<TimelineSkipReason, string> = {
  [TimelineSkipReason.NoManager]: "no manager",
  [TimelineSkipReason.OptedOut]: "opted out",
  [TimelineSkipReason.NotApplicable]: "not taking part",
  [TimelineSkipReason.Deactivated]: "deactivated",
  [TimelineSkipReason.ChatDisabled]: "no chat account",
  [TimelineSkipReason.AlreadyReminded]: "already reminded",
  [TimelineSkipReason.NotLocalSendHour]: "outside their send hour",
};

/**
 * Who one automatic message goes to, by the recipient rule the backend sends with
 * it (`RecipientRule` in `compliance/notification_catalog.py`).
 *
 * "3 people" made the admin work out who the three were. Each notification only
 * ever goes to one kind of person, so the row can name them instead. The rule is
 * the backend's; only the words are the page's. Keyed by every rule, so a rule
 * added there fails the type check here until it has words of its own.
 *
 * Takes the label function for the same reason `stepNames` does.
 */
export const recipientAudience = (
  label: UseLabelType,
): Record<TimelineRecipientRule, string> => ({
  [TimelineRecipientRule.AllParticipants]: "All participants",
  [TimelineRecipientRule.ParticipantsWithAManager]:
    "All participants with a manager",
  [TimelineRecipientRule.ParticipantReports]:
    "All direct reports of participants",
  [TimelineRecipientRule.NominatedPeer]: "All nominated peers",
  [TimelineRecipientRule.SubjectPrimaryManager]: `All managers in the ${label("review")}`,
  [TimelineRecipientRule.GlobalAdminsOptedIn]: "All admins who opted in",
  [TimelineRecipientRule.Subject]: "All participants with a package",
  [TimelineRecipientRule.AssessmentReviewer]: "The manager who wrote it",
});

export const channelLabels: Record<string, string> = {
  email: "email",
  slack: "Slack",
  teams: "Teams",
};

/**
 * The step's own name, short enough for a narrow column.
 *
 * These are `STEP_LABELS` from `compliance/simulation.py`, with the two
 * calibrations shortened: the section heading above a row already says whether it
 * is falling due or opening, so "Post-review calibration due" can lose two thirds
 * of itself and still say the same thing.
 *
 * Takes the label function rather than reading it, because this file is not a
 * component and `useLabel` is a hook. An organization that renamed reviews reads
 * its own word in the setup form's fields, so the Timeline beside them has to use
 * the same one or the two disagree about the same step on one screen.
 */
export const stepNames = (
  label: UseLabelType,
): Record<ComplianceProgramStep, string> => ({
  [ComplianceProgramStep.PreCalibration]: "Pre-calibration",
  [ComplianceProgramStep.PeerNomination]: "Peer nomination",
  [ComplianceProgramStep.SelfReview]: `Self ${label("review")}`,
  [ComplianceProgramStep.PeerReview]: `Peer ${label("review")}`,
  [ComplianceProgramStep.UpwardReview]: `Upward ${label("review")}`,
  [ComplianceProgramStep.DownwardReview]: `Manager ${label("review")}`,
  [ComplianceProgramStep.PostCalibration]: "Calibration",
  [ComplianceProgramStep.Delivery]: "Delivery",
  [ComplianceProgramStep.OneOnOne]: label("oneonone"),
  [ComplianceProgramStep.EngagementSurvey]: "Survey",
});

/**
 * The same names by the words the backend writes them with, to shorten a label.
 *
 * `compliance/simulation.py` builds its own labels from the organization's word
 * too, so the keys carry it as well. An organization that renamed reviews reads
 * "the self check-in completes" in the label, and a key of "self review" would
 * never match it.
 */
const stepNamesByBackendLabel = (
  label: UseLabelType,
): Record<string, string> => {
  const review = label("review");
  const names = stepNames(label);
  return {
    [`pre-${review} calibration`]: names[ComplianceProgramStep.PreCalibration],
    "peer nomination": names[ComplianceProgramStep.PeerNomination],
    [`self ${review}`]: names[ComplianceProgramStep.SelfReview],
    [`peer ${review}`]: names[ComplianceProgramStep.PeerReview],
    [`upward ${review}`]: names[ComplianceProgramStep.UpwardReview],
    [`manager ${review}`]: names[ComplianceProgramStep.DownwardReview],
    [`post-${review} calibration`]:
      names[ComplianceProgramStep.PostCalibration],
    delivery: names[ComplianceProgramStep.Delivery],
    [label("oneonone").toLowerCase()]: names[ComplianceProgramStep.OneOnOne],
    survey: names[ComplianceProgramStep.EngagementSurvey],
  };
};

/**
 * What the row is about, without the word the section heading already supplies.
 *
 * `compliance/simulation.py` writes these labels in one of three shapes — "X due",
 * "X opens", and "X opens as the Y completes" — so a row under a **Due** or an
 * **Opens** heading is only the step's name. A row that does not match any shape
 * keeps its whole label: a reworded backend then reads longer than it should,
 * never wrong.
 *
 * The fallback is the backend's own English, which says "review" whatever the
 * organization calls it. Every row the backend gives a `step` takes the branch
 * above instead, so the fallback is the rare one.
 */
export const stepOnly = (row: TimelineRow, label: UseLabelType) =>
  (row.step && stepNames(label)[row.step]) ??
  row.label.replace(/ (due|opens)$/, "");

/** The one shape `compliance/simulation.py` gives a step that waits on another. */
const opensAsCompletes = /^(.+) opens as the (.+) completes$/;

/**
 * "Manager review opens as the self review completes" → "Manager review after
 * self review", or null when the row is not one that waits on another step.
 *
 * A step with a predecessor does not really open on one day: it opens per person,
 * as that person's earlier work comes in. Saying "after the self review" keeps
 * that, which a bare date would lose.
 */
export const opensAfter = (row: TimelineRow, label: UseLabelType) => {
  const match = opensAsCompletes.exec(row.label);
  if (!match) {
    return null;
  }
  const predecessor = stepNamesByBackendLabel(label)[match[2]] ?? match[2];
  return `${(row.step && stepNames(label)[row.step]) ?? match[1]} after ${predecessor.toLowerCase()}`;
};

/** "Manager review opens as the self review completes" → "Self review". */
export const predecessorOf = (row: TimelineRow, label: UseLabelType) => {
  const match = opensAsCompletes.exec(row.label);
  return match ? (stepNamesByBackendLabel(label)[match[2]] ?? match[2]) : null;
};

/**
 * What an ongoing review's dates are, said before the admin reads one of them.
 *
 * Two sentences, because they answer two questions: what this review really does,
 * and what the dates below stand for. A date nobody typed must never look like one
 * they did.
 *
 * "Conditional" is what the form calls an ongoing review on the Workflow tab, so
 * it is the word the admin chose a moment before they read this.
 */
export const conditionalTimelineMessage = (noun: string, afterDays: number) => [
  `This is a conditional ${noun}. It kicks off ${afterDays} ${afterDays === 1 ? "day" : "days"} after each person's start date.`,
  `These dates show what it would do if it kicked off today.`,
];

/**
 * Who a row reaches, in words. Only a sending row has an audience, and a row whose
 * recipients nobody can know yet says so rather than reporting nobody.
 *
 * An automatic message names the kind of person it goes to. A reminder, which the
 * admin points at whoever they like, and a message reaching nobody keep the count.
 */
export const audience = (row: TimelineRow, label: UseLabelType) => {
  if (!sendingKinds.includes(row.kind)) {
    return null;
  }
  if (!row.recipientsKnown) {
    return "recipients not known yet";
  }
  if (row.recipientRule && row.recipientCount > 0) {
    return recipientAudience(label)[row.recipientRule];
  }
  return `${row.recipientCount} ${
    row.recipientCount === 1 ? "person" : "people"
  }`;
};
